import Analytics from '@/utils/analytics';
import i18n from '@/utils/i18n';
import React, { useCallback, useEffect, useState } from 'react';
import { AUTH_ROUTES } from '@/navigators/routes';
import { AUTH_STYLES, SCREEN_PADDING, TYPOGRAPHY } from '@/styles';
import { AuthPageContainer } from '@/components/auth-page-container';
import { AuthStackParams } from '@/navigators/types';
import { Button } from '@/components/button';
import { captureException } from '@/utils/sentry';
import { createStyles } from '@/contexts/theme-context';
import { formatPhoneNumber } from '@/utils/phone-number';
import { LoginMethod, VerificationMethod } from '@/enums';
import { SEND_ANSWER_PLACEHOLDER } from '@/utils/auth';
import { SessionService } from '@domain/auth/blocs/services/session-service';
import { SignInMethodActionSheet } from '@/components/sign-in-method-action-sheet';
import { StackScreenProps } from '@react-navigation/stack';
import { SupplierSignupText } from '@/components/supplier-signup-text';
import { Text } from 'react-native';
import { TextInput } from '@/components/text-input';
import { Type } from '@/components/toast';
import { useAuthStackContext } from '@/contexts/auth-stack-context';
import { useSendChallengeAnswer } from '@/hooks/use-send-challenge-answer';
import { useToasts } from '@/contexts/toast-context';

// Restrict resends to every 10 seconds
const RESEND_TIMEOUT_MS = 10000;
const VERIFICATION_CODE_LENGTH = 6;

enum ResendState {
  Resending,
  Resent,
}

type Props = StackScreenProps<AuthStackParams, AUTH_ROUTES.SignInCode>;

export const SignInCodeScreen: React.FC<Props> = ({ route }) => {
  const {
    translationsEnabled,
    loginRequestEnabled,
    navigateToBecomeCustomer,
    navigateToLoginRecovery,
  } = useAuthStackContext();
  const sendChallengeAnswer = useSendChallengeAnswer();
  const [styles] = useStyles();
  const { showErrorToast, showToast } = useToasts();

  const [code, setCode] = useState('');
  const [resendState, setResendState] = useState<ResendState | null>(null);
  const [showMethodSelector, setShowMethodSelector] = useState<boolean>(false);
  const [verifying, setVerifying] = useState(false);

  useEffect(() => {
    if (resendState === ResendState.Resent) {
      const resendTimeout = setTimeout(() => {
        setResendState(null);
      }, RESEND_TIMEOUT_MS);

      return () => clearTimeout(resendTimeout);
    }
  }, [resendState]);

  const user = route.params.user;
  const loginMethod = route.params.loginMethod;

  const requestNewCode = useCallback(
    async (verificationMethod: VerificationMethod) => {
      setResendState(ResendState.Resending);
      try {
        await sendChallengeAnswer(
          user,
          SEND_ANSWER_PLACEHOLDER,
          verificationMethod,
          translationsEnabled,
        );
        return setResendState(ResendState.Resent);
      } catch (e) {
        if (e instanceof Error) {
          if (e.message?.includes('HTTP 429')) {
            showErrorToast(i18n.t('toast.sign_in_code_rate_limited'));
          } else {
            showErrorToast();
            captureException(e);
          }
        }
      }
      setResendState(null);
    },
    [sendChallengeAnswer, user, showErrorToast, translationsEnabled],
  );

  if (!user) {
    return null;
  }
  const inputText = user.getUsername();

  const onContinue = async () => {
    if (code.length !== VERIFICATION_CODE_LENGTH) {
      showToast({
        content: i18n.t('toast.validation_error'),
        type: Type.WARNING,
      });
      return;
    }

    Analytics.track('sign_in.verification_code_form.submit', {
      code,
      ...(loginMethod === LoginMethod.Phone
        ? { phoneNumber: inputText }
        : { emailAddress: inputText }),
    });
    setVerifying(true);
    try {
      // Verify code and if the user is authenticated
      await sendChallengeAnswer(
        user,
        code,
        loginMethod === LoginMethod.Phone
          ? VerificationMethod.SMS
          : VerificationMethod.Email,
        translationsEnabled,
      );
      await SessionService.getCurrentSession();
    } catch (e) {
      showErrorToast(i18n.t('invalid_verification_code'));

      setCode('');
      setVerifying(false);
    }
  };

  return (
    <>
      <AuthPageContainer title={i18n.t('verification_code')}>
        <TextInput
          autoFocus
          containerStyle={AUTH_STYLES.inputContainer}
          keyboardType="number-pad"
          label={code ? i18n.t('code') : undefined}
          onChangeText={setCode}
          onSubmitEditing={onContinue}
          placeholder={i18n.t('code')}
          returnKeyType="next"
          rightElement={
            <Button
              disabled={resendState === ResendState.Resent}
              loading={resendState === ResendState.Resending}
              onPress={async () => {
                if (loginMethod === LoginMethod.Email) {
                  Analytics.track(
                    'sign_in_code.resend_verification_form.submit',
                    {
                      emailAddress: inputText,
                      verificationMethod: VerificationMethod.Email,
                    },
                  );
                  await requestNewCode(VerificationMethod.Email);
                } else {
                  setShowMethodSelector(true);
                }
              }}
              size="small"
              style={styles.resendButton}
              trackingData={{ name: 'sign_in_code.resend_verification_button' }}
              type="tertiary"
            >
              {resendState === ResendState.Resent
                ? i18n.t('resent')
                : i18n.t('resend_code')}
            </Button>
          }
          testID="code-input"
          textContentType="oneTimeCode"
          value={code}
        />
        <Text style={[TYPOGRAPHY.p, styles.message]}>
          {i18n.t('verification_code_message', {
            sentTo:
              route.params.loginMethod === LoginMethod.Phone
                ? formatPhoneNumber(inputText)
                : inputText,
          })}
        </Text>
        <Button
          containerStyle={styles.buttonContainer}
          loading={verifying}
          onPress={onContinue}
          testID="sign-in-code-submit-button"
          trackingData={{ name: 'sign_in_code.continue_button' }}
        >
          {i18n.t('continue')}
        </Button>
        <SupplierSignupText
          loginRequestEnabled={loginRequestEnabled}
          navigateToBecomeCustomer={navigateToBecomeCustomer}
          navigateToLoginRecovery={navigateToLoginRecovery}
        />
      </AuthPageContainer>
      <SignInMethodActionSheet
        isVisible={showMethodSelector === true}
        onClose={() => setShowMethodSelector(false)}
        onSelect={async (verificationMethod: VerificationMethod) => {
          Analytics.track('sign_in_code.resend_verification_form.submit', {
            phoneNumber: inputText,
            verificationMethod,
          });
          await requestNewCode(verificationMethod);
        }}
        phoneNumber={formatPhoneNumber(inputText)}
      />
    </>
  );
};

const useStyles = createStyles((theme) => ({
  buttonContainer: {
    marginBottom: SCREEN_PADDING,
  },
  message: {
    color: theme.colors.primary,
    marginVertical: 16,
    textAlign: 'center',
  },
  resendButton: {
    paddingRight: 0,
  },
}));
